import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { LogSummary } from "./types";
import { tokens, SEVERITY } from "./tokens";

interface SummaryCardsProps {
  summary?: LogSummary;
}

export default function SummaryCards({ summary }: SummaryCardsProps) {
  if (!summary) return null;

  const totalLogs = summary.totalLogs;
  const errors = summary.byLevel?.error ?? 0;
  const warnings = summary.byLevel?.warn ?? 0;
  const info = summary.byLevel?.info ?? 0;

  // Proportion of error logs (guard divide-by-zero).
  const errorPct = totalLogs > 0 ? Math.min(100, (errors / totalLogs) * 100) : 0;

  const captionSx = {
    color: tokens.textSecondary,
    letterSpacing: "0.04em",
    fontSize: "0.62rem",
    textTransform: "uppercase" as const,
    fontWeight: 600,
  };

  const numberSx = {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.25,
  };

  const cardSx = {
    backgroundColor: tokens.surface,
    border: `1px solid ${tokens.divider}`,
    borderRadius: 1.5,
    boxShadow: "none",
  };

  const contentSx = { py: 1.5, px: 2, "&:last-child": { pb: 1.5 } } as const;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5, mb: 2 }}>
      {/* Total */}
      <Card sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="caption" sx={captionSx}>
            Total
          </Typography>
          <Typography sx={{ ...numberSx, color: tokens.textPrimary }}>
            {totalLogs.toLocaleString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Errors (emphasized + proportion bar) */}
      <Card sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="caption" sx={captionSx}>
            Errors
          </Typography>
          <Typography sx={{ ...numberSx, color: SEVERITY.error.text }}>
            {errors.toLocaleString()}
          </Typography>
          <Box
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: tokens.elevated,
              border: `1px solid ${tokens.divider}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${errorPct}%`,
                backgroundColor: SEVERITY.error.text,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Warnings (dimmed when zero) */}
      <Card sx={{ ...cardSx, opacity: warnings === 0 ? 0.55 : 1 }}>
        <CardContent sx={contentSx}>
          <Typography variant="caption" sx={captionSx}>
            Warnings
          </Typography>
          <Typography sx={{ ...numberSx, color: SEVERITY.warning.text }}>
            {warnings.toLocaleString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Info (dimmed when zero) */}
      <Card sx={{ ...cardSx, opacity: info === 0 ? 0.55 : 1 }}>
        <CardContent sx={contentSx}>
          <Typography variant="caption" sx={captionSx}>
            Info
          </Typography>
          <Typography sx={{ ...numberSx, color: SEVERITY.info.text }}>
            {info.toLocaleString()}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
